/**
 * @Description
 * @Version
 * @Author Xu Zepeng
 * @date 2023/5/23 15:37
 */
/*
 * Print Service Methods
 * */

import PrintElementFactory from './PrintElementFactory'; // Assuming PrintElementFactory is also in TS or has typings
import Socket from './Socket'; // Assuming Socket.ts is the correct path and it's a TS file

interface PrinterErrorMessage {
    [key: number]: string;
}

interface JsonParameter {
    fontDir?: string;
    printerName?: string;
    port?: number;
    tcpPort?: number;
    wifiName?: string;
    wifiPassword?: string;
    nType?: number;
    paperType?: number;
    printSpeed?: number;
    printDensity?: number;
    printLabelType?: number;
    printMode?: number;
    count?: number;
    printData?: string;
    printerImageProcessingInfo?: number;
    elements?: Record<string, unknown>[];

}

interface ApiRequest {
    apiName: string;
    parameter?: JsonParameter;
}

interface PrintResponse {
    apiName: string;
    resultAck: {
        errorCode: number;
        info: string;
        result?: unknown;
    };
}

interface PrintMessage {
    apiName?: string;
    resultAck?: {
        errorCode?: number;
        info?: string;
        callback?: ((msg: PrintMessage) => void);
    };
}

interface SocketWithWebSocket {
    _websocket?: {
        readyState: number;
    };
}

export default class NMPrintSocket {
  // Print exception codes and reasons
  public printErrorMessage: PrinterErrorMessage = {
    1: 'Exception: Cover open, please close cover to continue',
    2: 'Label paper not detected, please reinstall and try again',
    3: 'Battery low, please charge and try again',
    4: 'Battery exception, please contact customer service',
    5: 'Printing stopped',
    6: 'Print exception, please try again later',
    7: 'Print head temperature too high, please cool down and try again',
    8: 'Paper output exception or out of paper, please check paper',
    9: 'Printer busy, please try again later',
    10: 'Print head not detected, please contact customer service',
    11: 'Ambient temperature too low, please try again at room temperature',
    12: 'Print head unlocked, please lock and try again',
    13: 'Ribbon not detected, please install ribbon and try again',
    14: 'Ribbon mismatch, please install matching ribbon',
    15: 'Ribbon out, please replace ribbon and try again',
    16: 'Paper type error, please contact customer service',
    20: 'Label damaged or non-RFID label, please check paper',
    22: 'Communication exception, please try again later',
    23: 'Printer disconnected, please reconnect and try again',
    28: 'Label paper exception, please press printer right button to recalibrate',
    29: 'RFID label detected, please replace paper and try again',
    30: 'Density setting exception, please reset',
    31: 'Print mode exception, please reset',
    32: 'Label material setting exception, please reset',
    33: 'Unsupported label material, please replace or reset',
    34: 'RFID write not supported',
    50: 'Illegal label',
    51: 'Illegal ribbon and label',
  };
  private printSocket: Socket;

  constructor(printSocketData: Socket) {
    this.printSocket = printSocketData;
  }

  // Get if print service is connected
  public printSocketStatus(): number | undefined {
    const websocket = (this.printSocket as unknown as SocketWithWebSocket)?._websocket;
    return websocket?.readyState;
  }

  // Custom message send, skipping promise
  public customSend(json: ApiRequest): void {
    return this.printSocket.customSend(json);
  }

  /**
     * Init SDK, call this interface after print service connected successfully.
     * Must call this interface before calling SDK draw interface.
     *
     * @param {object} json - JSON object containing necessary parameters, format as follows:
     *  {
     *   "fontDir": string, // Font file directory, default "", not effective yet
     * }
     *
     * @return {Promise} 返回一个 Promise，解析为初始化SDK的结果
     */
  public initSdk(json: { fontDir: string }): Promise<PrintResponse> {
    const jsonObj: ApiRequest = {
      apiName: 'initSdk',
      parameter: json,
    };

    return this.printSocket.send(jsonObj);
  }

  /**
     * Get all Niimbot printers connected via USB on current PC
     *
     * @return {Promise} Return a Promise resolving to printer list.
     *
     * @description
     * Call this function after print service connected successfully.
     */
  public getAllPrinters(): Promise<PrintResponse> {
    const jsonObj: ApiRequest = { apiName: 'getAllPrinters' };
    // console.log("开始搜索设备");
    return this.printSocket.send(jsonObj);
  }

  /**
     * Select and open printer name and port to use
     *
     * @param {string} printerName - Printer Name.
     * @param {number} port - Connection port.
     * @return {Promise} Return a Promise resolving to connection result
     *
     * @description
     * Call this function after print service connected successfully. Recommended to call after getAllPrinters success, to ensure printer name and port state are normal.
     */
  public selectPrinter(printerName: string, port: number): Promise<PrintResponse> {
    const jsonObj: ApiRequest = {
      apiName: 'selectPrinter',
      parameter: {
        printerName: printerName,
        port: port,
      },
    };
    return this.printSocket.send(jsonObj);
  }

  /**
     * Search Wifi printers
     *
     *
     * @return {Promise} Return a Promise resolving to printer Wifi config info
     *
     * @description
     * Call this function after print service connected successfully.
     */
  public scanWifiPrinter(): Promise<PrintResponse> {
    const jsonObj: ApiRequest = {
      apiName: 'scanWifiPrinter',
    };
    return this.printSocket.send(jsonObj, 25000);
  }

  /**
     * Send message to select printer.
     *
     * @param {string} printerName - Printer name.
     * @param {number} tcpPort - Port number.
     * @return {Promise} Return connection result
     *
     * @description
     * Call this function after print service connected successfully. Recommended to call in callback of scanWifiPrinter success, to ensure printer name and port state are normal.
     * Note: This function can only connect to printers in WIFI printer list.
     */
  public connectWifiPrinter(printerName: string, tcpPort: number): Promise<PrintResponse> {
    const jsonObj: ApiRequest = {
      apiName: 'connectWifiPrinter',
      parameter: {
        printerName: printerName,
        port: tcpPort, // API expects port, not tcpPort based on selectPrinter
      },
    };
    return this.printSocket.send(jsonObj, 25000);
  }

  /**
     * Configure printer Wifi network
     *
     * @param {string} wifiName - Wifi network name.
     * @param {string} wifiPassword - Wifi network password.
     * @return {Promise} Return a Promise resolving to printer Wifi config result
     *
     * @description
     * Note: Only 2.4G frequency band supported, configure after connection success. First config recommeded after USB connection success.
     */
  public configurationWifi(wifiName: string, wifiPassword: string): Promise<PrintResponse> {
    const jsonObj: ApiRequest = {
      apiName: 'configurationWifi',
      parameter: {
        wifiName: wifiName,
        wifiPassword: wifiPassword,
      },
    };
    return this.printSocket.send(jsonObj, 25000);
  }

  /**
     * Get printer wifi configuration.
     *
     * @return {Promise} Return a Promise resolving to printer Wifi config info
     */
  public getWifiConfiguration(): Promise<PrintResponse> {
    const jsonObj: ApiRequest = {
      apiName: 'getWifiConfiguration',
    };
    return this.printSocket.send(jsonObj);
  }

  /**
     * Close printer.
     *
     * @return {Promise} Return a Promise resolving to close result
     */
  public closePrinter(): Promise<PrintResponse> {
    const jsonObj: ApiRequest = { apiName: 'closePrinter' };
    return this.printSocket.send(jsonObj);
  }

  /**
     * Set printer auto shutdown time.
     *
     * @param {number} nType - Auto shutdown level:
     *                        1: 15 minutes
     *                        2: 30 minutes
     *                        3: 60 minutes
     *                        4: Never
     * @return {Promise} - Return Promise resolving to result
     */
  public setPrinterAutoShutDownTime(nType: 1 | 2 | 3 | 4): Promise<PrintResponse> {
    const jsonObj: ApiRequest = {
      apiName: 'setPrinterAutoShutDownTime',
      parameter: { nType: nType },
    };
    // console.log("设置自动关机", jsonObj);
    return this.printSocket.send(jsonObj);
  }


  /**
     * Start a print job.
     *
     * @param {number} printDensity - Print density, range varies by model:
     *                                  - B3S, B203, B1, K3, K3W, M2: Range 1~5, default 3.
     *                                    - B50, B11, B50W, B32, Z401: Range 1~15, default 8.
     * @param {number} printLabelType - Paper type options:
     *                               1: Gap paper
     *                               2: Black mark paper
     *                               3: Continuous paper
     *                               4: Perforated paper
     *                               5: Transparent paper
     *                               6: Signage
     *                              10: Black mark gap paper
     * @param {number} printMode - Print mode options:
     *                            1: Thermal
     *                            2: Thermal Transfer
     *                            Note: specific models support specific modes:
     *                            - D11, D101, D110, H10, B16, B18, B3S, B203, B1, K3, K3W, B11 support Thermal only.
     *                            - B50, B50W, B32, Z401, M2 support Thermal Transfer only.
     * @param {number} count - Total copies, sum of copies of all pages.
     *                            E.g. 3 pages: copies 3, 2, 5 -> count=10.
     * @return {Promise} - Return Promise resolving to result
     * @example
     * // Return data example
     * {
     *     "apiName": "startJob",
     *     "resultAck": {
     *         "errorCode": 0,
     *         "info": "startJob ok!",
     *         "result": 0
     *     }
     * }
     * @description errorCode meaning:
     *              - 0: Success
     *              - -1: Failed, info for reason
     *              - -2: Printer busy, info for reason
     *              - -3: Unsupported params (density/type/mode), info for details
     */
  public startJob(
    printDensity: number,
    printLabelType: number,
    printMode: number,
    count: number
  ): Promise<PrintResponse> {
    const jsonObj: ApiRequest = {
      apiName: 'startJob',
      parameter: {
        printDensity: printDensity,
        printLabelType: printLabelType,
        printMode: printMode,
        count: count,
      },
    };
    return this.printSocket.send(jsonObj);
  }


  /**
     * Commit a print job.
     *
     * @param {string} [printData=null] - Print data JSON string.
     * @param {string} printerImageProcessingInfo - Printer image processing info JSON string, containing copy count:
     * {
     *   "printerImageProcessingInfo": {
     *     "printQuantity": 1 // Specify copies for current page. E.g. 3 pages (3, 2, 5 copies): pass 3, 2, 5 respectively.
     *   }
     * }
     * @return {Promise} - Return Promise resolving to result
     */
  public commitJob(printData: string | undefined, printerImageProcessingInfo: string): Promise<PrintResponse> {
    const printDataJson: unknown = printData ? JSON.parse(printData) : undefined;
    // eslint-disable-next-line no-console
    console.log('Print Data', printDataJson);
    const jsonObj: ApiRequest = {
      apiName: 'commitJob',
      parameter: {
        printData: printDataJson as string | undefined,
        printerImageProcessingInfo: JSON.parse(printerImageProcessingInfo)['printerImageProcessingInfo'],
      },
    };
    return this.printSocket.send(jsonObj);
  }

  /**
     * End a print job.
     *
     * @return {Promise} - Return Promise resolving to result
     */
  public endJob(): Promise<PrintResponse> {
    const jsonObj: ApiRequest = { apiName: 'endJob' };
    return this.printSocket.send(jsonObj);
  }

  /**
     * Cancel current print job and execute callback.
     *
     * @return {Promise} Return Promise resolving to result
     */
  public cancelJob(): Promise<PrintResponse> {
    const jsonObj = { apiName: 'stopPrint' };
    return this.printSocket.send(jsonObj);
  }

  /**
     * Initialize drawing board.
     *
     * @param {Object} json - JSON object containing initialization data. Format:
     * {
     *   "width": number, // Board width, unit mm
     *   "height": number, // Board height, unit mm
     *   "rotate": number, // Rotation angle, only 0, 90, 180, 270
     *   "path": string, // Font file path, default "", not effective yet
     *   "verticalShift": number, // Vertical shift, not effective yet
     *   "HorizontalShift": number // Horizontal shift, not effective yet
     * }
     * @return {Promise} Return Promise resolving to result
     *
     * @description
     * Interface notes:
     * 1. Must initialize SDK before calling draw interface.
     * 2. Must initialize board before drawing elements, otherwise crash!
     * 3. Initialization clears previous content!
     */
  public InitDrawingBoard(json: string): Promise<PrintResponse> {
    const jsonObj = {
      apiName: 'InitDrawingBoard',
      parameter: JSON.parse(json),
    };
    return this.printSocket.send(jsonObj);
  }

  /**
     * Draw label text.
     * @param {object} json - JSON object containing text info.
     *   JSON format:
     *   - x: x coordinate, mm
     *   - y: y coordinate, mm
     *   - height: text height, mm
     *   - width: text width, mm
     *   - value: text content
     *   - fontFamily: font name, not effective yet, uses default Source Han Sans
     *   - rotate: rotation, 0:0, 1:90, 2:180, 3:270
     *   - fontSize: font size, mm
     *   - textAlignHorizonral: horizontal align: 0:left 1:center 2:right
     *   - textAlignVertical: vertical align: 0:top 1:middle 2:bottom
     *   - letterSpacing: letter spacing, mm
     *   - lineSpacing: line spacing (multiplier), default 1
     *   - lineMode: 1: fixed w/h, content auto-fit (scale font size)
     *     2: fixed width, auto height
     *     4: fixed w/h, clip excess
     *     6: fixed w/h, auto-scale if content exceeds w/h
     *     Recommended 6
     *   - fontStyle: font style [bold, italic, underline, strikethrough (reserved)]
     *
     * @return {Promise} Return Promise resolving to result
     * @description Must initialize board before drawing
     */
  public DrawLabelText(json: string): Promise<PrintResponse> {
    const elementConfig = PrintElementFactory.createElement('text', JSON.parse(json));
    return this.printSocket.send(elementConfig);
  }


  /**
     * Draw 1D barcode.
     *
     * @param {Object} json - JSON object containing barcode info. Format:
     * {
     *   "x": number, // x coordinate, mm
     *   "y": number, // y coordinate, mm
     *   "height": number, // barcode width, mm
     *   "width": number, // barcode height, mm (including text)
     *   "value": string, // barcode content
     *   "codeType": number, // barcode type:
     *                     // 20: CODE128
     *                     // 21: UPC-A
     *                     // 22: UPC-E
     *                     // 23: EAN8
     *                     // 24: EAN13
     *                     // 25: CODE93
     *                     // 26: CODE39
     *                     // 27: CODEBAR
     *                     // 28: ITF25
     *   "rotate": number, // rotation, 0: 0, 1: 90, 2: 180, 3: 270
     *   "fontSize": number, // text font size, mm, 0 to hide text
     *   "textHeight": number, // text height, mm, 0 to hide text
     *   "textPosition": number // HRI position:
     *                          // 0: Below
     *                          // 1: Above
     *                          // 2: None
     * }
     *
     * @return {Promise} Return Promise resolving to result
     *
     * @description
     * 1. Must initialize board before drawing
     */
  public DrawLabelBarCode(json: string): Promise<PrintResponse> {
    const elementConfig = PrintElementFactory.createElement('barCode', JSON.parse(json));
    return this.printSocket.send(elementConfig);
  }

  /**
     * Draw QR Code.
     *
     * @param {Object} json - JSON object containing QR code info. Format:
     * {
     *   "x": number, // x coordinate, mm
     *   "y": number, // y coordinate, mm
     *   "height": number, // QR code height, default same as width
     *   "width": number, // QR code width, mm
     *   "value": string, // QR code content
     *   "codeType": number, // barcode type:
     *                     // 31: QR_CODE
     *                     // 32: PDF417
     *                     // 33: DATA_MATRIX
     *                     // 34: AZTEC
     *   "rotate": number, // rotation, only 0, 90, 180, 270
     * }
     *
     * @return {Promise} Return Promise resolving to result
     *
     * @description
     * 1. Must initialize board before drawing
     */
  public DrawLabelQrCode(json: string): Promise<PrintResponse> {
    const elementConfig = PrintElementFactory.createElement('qrCode', JSON.parse(json));
    return this.printSocket.send(elementConfig);
  }

  /**
     * Draw QR Code with Logo.
     * @param {*} json - JSON object containing QR code info. Format:
     * {
     *   "x": number, // x coordinate, mm
     *   "y": number, // y coordinate, mm
     *   "height": number, // QR code height, default same as width
     *   "width": number, // QR code width, mm
     *   "value": string, // QR code content
     *   "codeType": number, // barcode type:
     *                     // 31: QR_CODE
     *                     // 32: PDF417
     *                     // 33: DATA_MATRIX
     *                     // 34: AZTEC
     *   "rotate": number, // rotation, only 0, 90, 180, 270
     *  "correctLevel": 2,// Error correction level, 1-4, default 2
     *  "logoBase64": string,// Logo base64 encoding (without header, e.g. data:image/png;base64,)
     *  "logoPosition": 0,// Logo position, 0-4, default 0:center, 3:bottom-right
     *  "logoHeight": number,// Logo height, mm, default 10mm
     *  "logoScale": 0.25,// Logo scale, 0-0.33, default 0.25
     * }
     */
  public DrawLabelQrCodeWithLogo(json: string): Promise<PrintResponse> {
    const elementConfig = PrintElementFactory.createElement('qrCodeWithLogo', JSON.parse(json));
    return this.printSocket.send(elementConfig);
  }

  /**
     * Draw Line.
     *
     * @param {Object} json - JSON object containing line info. Format:
     * {
     *   "x": number, // x coordinate, mm
     *   "y": number, // y coordinate, mm
     *   "height": number, // line height, mm
     *   "width": number, // line width, mm
     *   "lineType": number, // line type: 1:solid 2:dashed
     *   "rotate": number, // rotation, only 0, 90, 180, 270
     *   "dashwidth": number // dash width, [solid length, gap length]
     * }
     *
     * @return {Promise} Return Promise resolving to result
     *
     * @description
     * 1. Must initialize board before drawing
     */
  public DrawLabelLine(json: string): Promise<PrintResponse> {
    const elementConfig = PrintElementFactory.createElement('line', JSON.parse(json));
    return this.printSocket.send(elementConfig);
  }

  /**
     * Draw Graph (Shape).
     *
     * @param {Object} json - JSON object containing shape info. Format:
     * {
     *   "x": number, // x coordinate, mm
     *   "y": number, // y coordinate, mm
     *   "height": number, // shape height, mm
     *   "width": number, // shape width, mm
     *   "rotate": number, // rotation, only 0, 90, 180, 270
     *   "cornerRadius": number, // corner radius, mm, not effective yet
     *   "lineWidth": number, // line width, mm
     *   "lineType": number, // line type: 1:solid 2:dashed
     *   "graphType": number, // shape type: 1:circle 2:ellipse 3:rectangle 4:rounded rectangle
     *   "dashwidth": number // dash width, [solid length, gap length]
     * }
     *
     * @return {Promise} Return Promise resolving to result
     *
     * @description
     * 1. Must initialize board before drawing
     */
  public DrawLabelGraph(json: string): Promise<PrintResponse> {
    const elementConfig = PrintElementFactory.createElement('graph', JSON.parse(json));
    return this.printSocket.send(elementConfig);
  }

  /**
     * Draw Image.
     *
     * @param {Object} json - JSON object containing image info. Format:
     * {
     *   "x": number, // x coordinate, mm
     *   "y": number, // y coordinate, mm
     *   "height": number, // image height, mm
     *   "width": number, // image width, mm
     *   "rotate": number, // rotation, only 0, 90, 180, 270
     *   "imageProcessingType": number, // image processing algo, default 0
     *   "imageProcessingValue": number, // algo param, default 127
     *   "imageData": number, // image base64 data, without header
     *                     // e.g. "iVBORw0KGgoAAAANSU"
     * }
     * @return {Promise} Return Promise resolving to result
     *
     * @description
     * Note:
     * 1. Must initialize board before drawing
     */
  public DrawLabelImage(json: string): Promise<PrintResponse> {
    const elementConfig = PrintElementFactory.createElement('image', JSON.parse(json));
    return this.printSocket.send(elementConfig);
  }

  /**
     * Generate preview image.
     *
     * @param {number} displayScale - Display scale, dots per mm.
     *                                e.g. 200dpi printer: 8, 300dpi printer: 11.81.
     *
     * @return {Promise} Return Promise resolving to result
     *
     * @description
     * Note:
     * 1. Must ensure image data is ready before calling this, otherwise cannot generate preview.
     */
  public generateImagePreviewImage(displayScale: number): Promise<PrintResponse> {
    const jsonObj = {
      apiName: 'generateImagePreviewImage',
      displayScale: displayScale,
    };

    return this.printSocket.send(jsonObj);
  }

  // Add print listener
  public addPrintListener(callback: (msg: PrintMessage) => void): (msg: PrintMessage) => void {
    return this.printSocket.addPrintListener(callback);
  }

  // Remove print listener
  public removePrintListener(callback: (msg: PrintMessage) => void): void {
    this.printSocket.removePrintListener(callback);
  }

  // Add debug method
  public getListenersCount(): number {
    return this.printSocket.printListeners.size;
  }

  // Modify method to clear all listeners
  public clearAllListeners(): void {
    const count = this.printSocket.printListeners.size;
    this.printSocket.printListeners.clear();
    // eslint-disable-next-line no-console
    console.log(`Cleared ${count} listeners`);
  }


}


